import StringExtensions from "utils/stringExtensions";
import { UrlSubdirectoryTypes } from 'features/searchResultsFacets';

/**
 * Merges default configuration values with overrides.
 * @param {object} defaults - The default configuration values.
 * @param {object} [overrides={}] - The override values to merge with defaults.
 * @returns {object} - The merged configuration object.
 */
const mergeOverrides = (defaults, overrides = {}) => ({
  urlSubdirectory: overrides.urlFunction ?? defaults.urlSubdirectory,
  urlLocation: overrides.urlLocation ?? defaults.urlLocation,
  urlPaging: overrides.urlPaging ?? defaults.urlPaging,
  urlBuilderName: overrides.urlBuilderName ?? defaults.urlBuilderName,
  urlQueryString: overrides.urlQueryString ?? defaults.urlQueryString,
  urlHash: overrides.urlHash ?? defaults.urlHash
});

/**
 * Normalizes the configuration object by applying specific overrides
 * based on the subdirectory type.
 * @param {object} config - The original configuration object.
 * @returns {object} - The normalized configuration object.
 */
const normalizeConfig = (config) => {
  const overrides = UrlSubdirectoryTypes.fromKey(config.urlSubdirectory);
  return mergeOverrides(config, overrides);
};

/**
 * buildUrlPathName: Builds the pathname of the new URL.
 * @param {string} subdirectory - Segment related to the search subdirectory.
 * @param {string} location - Segments related to the search location.
 * @param {string} paging - Segment related to the search page.
 * @param {string} builderName - Segment related to the builder name.
 * @returns {string} - A string with the pathname of the search.
 */
function buildUrlPathName(subdirectory, location, paging, builderName) {
  // The order to build the current search results url pathname is
  // subdirectory segment, then the location segments and finally the paging segment.
  const urlSegments = [];
  if(subdirectory) {
    urlSegments.push(subdirectory);
  }

  if(location) {
    urlSegments.push(location);
  }

  if(builderName) {
    urlSegments.push(builderName);
  }

  if (paging) {
    urlSegments.push(paging);
  }

  return urlSegments.join('/');
}

/**
 * Creates a URL object based on a normalized configuration.
 * @param {object} config - The normalized configuration object.
 * @returns {URL} - The constructed URL object.
 */
const createUrl = (config) => {
  const path = buildUrlPathName(
    config.urlSubdirectory,
    config.urlLocation,
    config.urlPaging,
    config.urlBuilderName
  );

  const url = new URL(path, window.location.origin);
  url.search = config.urlQueryString || '';

  if (config.urlHash) {
    url.hash = config.urlHash;
  }

  return url;
};

/**
 * Main function that orchestrates the URL building process.
 * It normalizes the configuration and creates the final URL.
 * @param {object} config - The original configuration object.
 * @returns {URL} - The final constructed URL object.
 */
const buildNewUrl = (config) => {
  const normalizedConfig = normalizeConfig(config);
  return createUrl(normalizedConfig);
};

/**
 * getPaging: Gets the paging segment for the search.
 * @param {Number} pageNumber - Number of the selected page.
 * @returns {string} - The segment related to the paging of the current search.
 */
function getPaging(pageNumber) {
  // We only add the paging segment when the selected page is 2 or more.
  const shouldShowPagingOnUrl = pageNumber > 1;
  if (!shouldShowPagingOnUrl) {
    return '';
  }

  const newPaging = `page-${pageNumber}`;
  return newPaging;
}

/**
 * Returns a formatted brand name.
 *
 * @param {string} brandName - The name of the brand.
 * @param {number} brandId - The ID of the brand.
 * @returns {string} - The formatted brand name.
 */
function getBrandName(brandName, brandId) {
  if (!brandName || !brandId) {
    // If either brandName or brandId is not provided, return an empty string
    return '';
  }

  // Remove any special characters from the brandName
  const name = StringExtensions.encodeNameToUrl(brandName);

  // Return the formatted brand name with the builder prefix and brandId
  return `builder-${name}-${brandId}`;
}

export { buildNewUrl, getPaging, buildUrlPathName, getBrandName };